import type {EventCardProps} from '@commercial-helios/ui/src/components/event-card'
import {EventCard} from '@commercial-helios/ui/src/components/event-card'
import {tokens, colors} from '@commercial-helios/ui/theme'
import {matchSorter} from 'match-sorter'
import React from 'react'
import styled, {css} from 'styled-components'

import {CardsCarousel} from '@/components/cards-carousel'
import type {Props as PrismicLinkProps} from '@helios/shared/components/prismic-link'
import PrismicLink from '@helios/shared/components/prismic-link'
import Select from '@helios/shared/components/select'
import useMediaQuery from '@helios/shared/hooks/use-media-query'
import {Search} from '@helios/shared/images/icons/search'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {Container, Layout, Section} from '@helios/shared/styles/grid'
import {inputStyles} from '@helios/shared/styles/inputs'
import {NavTextLink, text, Text, TextLink} from '@helios/shared/styles/typography'
import {focusStyles} from '@helios/shared/styles/utils'
import mq from '@helios/shared/utils/media-query'

type OptionType = Pick<EventCardProps, 'category' | 'location' | 'language'>

type Filter = {
    name: keyof OptionType
    placeholder: string
    allLabel: string
}

type Props = {
    title: string
    topLink: PrismicLinkProps
    topLinkText: string
    layout: string
    eventsData: EventCardProps[]
    filters: Filter[]
    searchPlaceholder: string
    eventCtaText: string
    showingAllResultsLabel: string
    showingResultsForLabel: string
    clearText: string
    noEventsFoundText: string
}

const All_VALUE = 'all'

const getSelectOptions = (items: EventCardProps[], key: keyof OptionType, allLabel: string) => {
    const options = [{label: allLabel, value: All_VALUE}]
    const uniqueOptions = Array.from(new Set(items.map((item) => item[key])))

    uniqueOptions.forEach((option) => option && options.push({label: option, value: option}))

    return options
}

const useGetFilteredEvents = (
    events: EventCardProps[],
    searchTerm: string,
    filterValues: {[key: string]: string}
) => {
    return React.useMemo(() => {
        let results = events
        const keywordSearchString = searchTerm
        const filterString: string[] = []

        if (keywordSearchString) {
            results = matchSorter(events, keywordSearchString, {
                keys: [{threshold: matchSorter.rankings.CONTAINS, key: '*.title'}],
            })
        }

        Object.keys(filterValues).forEach((key) => {
            const filterKey = key as keyof OptionType

            if (filterValues[filterKey] && filterValues[filterKey] !== All_VALUE) {
                results = results.filter((result) => result[filterKey] === filterValues[filterKey])
                filterString.push(filterValues[filterKey])
            }
        })

        const activeFilters =
            keywordSearchString || filterString.length
                ? `${keywordSearchString} ${filterString.join(', ')}`
                : null

        return {results, activeFilters}
    }, [events, searchTerm, filterValues])
}

const EVENTS_PER_BATCH = 6

export const EventCardsWithFilters = ({
    title,
    topLink,
    topLinkText,
    layout = 'grid',
    eventsData,
    filters,
    searchPlaceholder,
    showingAllResultsLabel,
    showingResultsForLabel,
    clearText,
    noEventsFoundText,
}: Props) => {
    const isSmallScreen = useMediaQuery.max('sm')

    const filtersAllState = filters.reduce((acc, {name}) => ({...acc, [name]: All_VALUE}), {})
    const [searchTerm, setSearchTerm] = React.useState('')
    const [filterValues, setFilterValues] = React.useState<{[key: string]: string}>({})
    const [visibleEvents, setVisibleEvents] = React.useState(EVENTS_PER_BATCH)
    const globalStrings = useGlobalStrings()

    const {results, activeFilters} = useGetFilteredEvents(eventsData, searchTerm, filterValues)

    const clearFilters = () => {
        setSearchTerm('')
        setFilterValues(filtersAllState)
    }

    const showMorePosts = () => {
        setVisibleEvents((numberOfEvents) => numberOfEvents + EVENTS_PER_BATCH)
    }

    const hasActiveFilters =
        searchTerm ||
        Object.values(filterValues).some((filter) => filter !== '' && filter !== All_VALUE)

    const isGrid = layout === 'grid'

    return (
        <Section edgeToEdge padding={isGrid ? 'small' : 'regular'}>
            <TitleAndLink $layout={isGrid ? 'row' : 'column'}>
                <Text
                    as="h2"
                    variant="heading-base"
                    center={isGrid}
                    style={{order: isGrid ? 2 : 0}}
                >
                    {title}
                </Text>

                <div>
                    {isGrid && (
                        <NavTextLink variant="paragraph-base" as={PrismicLink} {...topLink}>
                            {topLinkText}
                        </NavTextLink>
                    )}

                    {!isGrid && !isSmallScreen && (
                        <TextLink variant="paragraph-large" as={PrismicLink} {...topLink}>
                            {topLinkText}
                        </TextLink>
                    )}
                </div>
            </TitleAndLink>

            <FiltersWrapper>
                <SearchInputWrapper>
                    <SearchIcon aria-hidden />

                    <input
                        autoFocus
                        type="search"
                        value={searchTerm.trim()}
                        placeholder={searchPlaceholder}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        autoComplete="off"
                    />
                </SearchInputWrapper>

                {filters.map((filter) => (
                    <Select
                        key={filter.name}
                        options={getSelectOptions(eventsData, filter.name, filter.allLabel)}
                        value={filterValues[filter.name] || ''}
                        placeholder={filter.placeholder}
                        onChange={(value) =>
                            setFilterValues({...filterValues, [filter.name]: value})
                        }
                    />
                ))}
            </FiltersWrapper>

            <div aria-live="polite">
                <SearchResultsStringWrapper>
                    <Text variant="paragraph-base" color="lightMuted">
                        {activeFilters
                            ? `${showingResultsForLabel} ${activeFilters}`
                            : showingAllResultsLabel}
                    </Text>
                    {hasActiveFilters && (
                        <ClearFiltersButton onClick={clearFilters}>{clearText}</ClearFiltersButton>
                    )}
                </SearchResultsStringWrapper>

                {results.length > 0 ? (
                    <>
                        {layout === 'carousel' ? (
                            <CardsCarousel>
                                {results.map((event) => (
                                    <StyledEventCard key={event.title} {...event} />
                                ))}
                            </CardsCarousel>
                        ) : (
                            <StyledContainer>
                                <Layout variant="three-equal" alignItems="stretch">
                                    {results.slice(0, visibleEvents).map((event) => (
                                        <StyledEventCard key={event.title} {...event} />
                                    ))}
                                </Layout>

                                {visibleEvents < results.length && (
                                    <TextLink as="button" onClick={showMorePosts}>
                                        {globalStrings.show_more}
                                    </TextLink>
                                )}
                            </StyledContainer>
                        )}
                    </>
                ) : (
                    <Container>
                        <Text variant="title-base">{noEventsFoundText}</Text>
                    </Container>
                )}
            </div>

            {!isGrid && isSmallScreen && (
                <MobileLinkContainer>
                    <TextLink variant="paragraph-large" as={PrismicLink} {...topLink}>
                        {topLinkText}
                    </TextLink>
                </MobileLinkContainer>
            )}
        </Section>
    )
}

const MOBILE_BREAKPOINT = 'sm'

const FiltersWrapper = styled(Container)`
    display: grid;
    gap: ${tokens.spacing20};
    margin-top: ${tokens.spacing20};

    ${mq.min(MOBILE_BREAKPOINT)} {
        grid-auto-flow: column;
        grid-template-columns: 1fr 0.5fr 0.5fr;
        margin-top: ${tokens.spacing40};
    }
`

const SearchResultsStringWrapper = styled(Container)`
    display: grid;
    grid-auto-flow: row;
    gap: ${tokens.spacing8};
    margin-top: ${tokens.spacing20};
    margin-bottom: ${tokens.spacing20};

    ${mq.min(MOBILE_BREAKPOINT)} {
        gap: ${tokens.spacing14};
        grid-auto-flow: column;
        justify-content: left;
        margin-top: 80px;
        margin-bottom: 80px;
    }
`

const ClearFiltersButton = styled.button`
    all: unset;
    cursor: pointer;
    ${text({color: 'brand', variant: 'paragraph-base'})}

    :focus {
        ${focusStyles()}
    }
`

const StyledContainer = styled(Container)`
    display: grid;
    justify-items: center;
    gap: ${tokens.spacing20};

    ${mq.min(MOBILE_BREAKPOINT)} {
        gap: ${tokens.spacing40};
    }
`

type StyledContainerProps = {
    $layout: 'row' | 'column'
}

const gridLayoutStyles = css`
    grid-template-rows: 1fr auto;
    padding-bottom: 80px;
`

const carouselLayoutStyles = css`
    grid-template-columns: 1fr auto;
    gap: ${tokens.spacing20};
    align-items: flex-start;
`

const TitleAndLink = styled(Container)<StyledContainerProps>`
    display: grid;
    grid-column-gap: ${tokens.spacing20};
    grid-row-gap: ${tokens.spacing60};

    ${(props) => (props.$layout === 'row' ? gridLayoutStyles : carouselLayoutStyles)}
`

const MobileLinkContainer = styled(Container)`
    margin-top: ${tokens.spacing20};
`

const iconSpacingLeft = tokens.spacing20
const iconSpacingRight = tokens.spacing12
const iconSize = '20px'

const SearchInputWrapper = styled.div`
    width: 100%;
    position: relative;
    display: flex;
    align-items: center;

    & > input {
        ${inputStyles}
        padding-left: calc(${iconSize} + ${iconSpacingLeft} + ${iconSpacingRight});
    }
`

const SearchIcon = styled(Search)`
    position: absolute;
    left: ${iconSpacingLeft};
    width: ${iconSize};
    height: ${iconSize};
    color: ${tokens.shade600};
    pointer-events: none;

    ${SearchInputWrapper}:focus-within & {
        color: ${tokens.shade700};
    }
`

const StyledEventCard = styled(EventCard)`
    padding: ${tokens.spacing20};
    border: 1px solid ${colors.border.dark};

    ${mq.min(MOBILE_BREAKPOINT)} {
        padding: ${tokens.spacing28};
    }
`
